/**
 * TipTapToolbar (T023 - US2)
 * Toolbar UI with buttons for all TipTap formatting options
 */

import { useState, useRef } from 'react';
import type { Editor } from '@tiptap/react';
import { ToolbarUrlInput } from './ToolbarUrlInput';
import { ToolbarColorPicker } from './ToolbarColorPicker';
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Quote,
  Code,
  Minus,
  Table,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link,
  ImageIcon,
  MonitorPlay,
} from 'lucide-react';

type TipTapToolbarProps = {
  editor: Editor | null;
};

type ToolbarButtonProps = {
  onClick: () => void;
  isActive?: boolean;
  label: string;
  children: React.ReactNode;
};

function ToolbarButton({ onClick, isActive = false, label, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={isActive}
      className={`p-1.5 rounded transition-colors ${
        isActive
          ? 'bg-[#026857] text-white'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      }`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <div className="w-px h-6 bg-gray-200 mx-1" />;
}

export function TipTapToolbar({ editor }: TipTapToolbarProps) {
  const [activeInput, setActiveInput] = useState<'link' | 'image' | 'embed' | null>(null);
  const [inputUrl, setInputUrl] = useState('');
  const savedSelection = useRef<{ from: number; to: number } | null>(null);

  if (!editor) return null;

  const openInput = (type: 'link' | 'image' | 'embed') => {
    const { from, to } = editor.state.selection;
    savedSelection.current = { from, to };
    setInputUrl(type === 'link' ? (editor.getAttributes('link').href || '') : '');
    setActiveInput(type);
  };

  const closeInput = () => {
    setActiveInput(null);
    setInputUrl('');
    savedSelection.current = null;
    editor.chain().focus().run();
  };

  const applyInput = (urlOverride?: string) => {
    const url = (urlOverride || inputUrl).trim();
    if (!url) { closeInput(); return; }
    const href = url.startsWith('http') ? url : `https://${url}`;
    const sel = savedSelection.current;

    if (activeInput === 'link' && sel) {
      editor.chain().focus().setTextSelection(sel).setLink({ href }).run();
    } else if (activeInput === 'image') {
      editor.chain().focus().setImage({ src: href }).run();
    } else if (activeInput === 'embed') {
      editor.chain().focus().insertContent({ type: 'iframe', attrs: { src: href } }).run();
    }
    setActiveInput(null);
    setInputUrl('');
    savedSelection.current = null;
  };

  const handleLink = () => {
    if (editor.isActive('link')) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    openInput('link');
  };

  const handleTable = () => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  const iconSize = 16;

  return (
    <div
      data-testid="tiptap-toolbar"
      className="flex flex-wrap items-center gap-0.5 px-3 py-2 border-b border-gray-200 bg-gray-50"
    >
      {/* Text formatting */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive('bold')}
        label="Bold"
      >
        <Bold size={iconSize} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive('italic')}
        label="Italic"
      >
        <Italic size={iconSize} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        isActive={editor.isActive('underline')}
        label="Underline"
      >
        <Underline size={iconSize} />
      </ToolbarButton>
      <ToolbarColorPicker editor={editor} />

      <Divider />

      {/* Heading level dropdown (G2-4: h1-h6 + Normal text) */}
      <select
        value={
          editor.isActive('heading', { level: 1 }) ? '1' :
          editor.isActive('heading', { level: 2 }) ? '2' :
          editor.isActive('heading', { level: 3 }) ? '3' :
          editor.isActive('heading', { level: 4 }) ? '4' :
          editor.isActive('heading', { level: 5 }) ? '5' :
          editor.isActive('heading', { level: 6 }) ? '6' : ''
        }
        onChange={(e) => {
          const level = Number(e.target.value) as 1 | 2 | 3 | 4 | 5 | 6;
          if (level) editor.chain().focus().toggleHeading({ level }).run();
          else editor.chain().focus().setParagraph().run();
        }}
        className="h-7 text-xs border border-gray-200 rounded px-1 bg-white text-gray-600 focus:outline-none focus:ring-1 focus:ring-[#026857]"
        aria-label="Heading level"
      >
        <option value="">Normal</option>
        <option value="1">Heading 1</option>
        <option value="2">Heading 2</option>
        <option value="3">Heading 3</option>
        <option value="4">Heading 4</option>
        <option value="5">Heading 5</option>
        <option value="6">Heading 6</option>
      </select>

      <select
        value={editor.getAttributes('textStyle').fontFamily || ''}
        onChange={(e) => {
          if (e.target.value) editor.chain().focus().setFontFamily(e.target.value).run();
          else editor.chain().focus().unsetFontFamily().run();
        }}
        className="h-7 text-xs border border-gray-200 rounded px-1 bg-white text-gray-600 focus:outline-none focus:ring-1 focus:ring-[#026857] ml-1 max-w-[90px]"
        aria-label="Font family"
      >
        <option value="">Font</option>
        <option value="termina, sans-serif">Termina</option>
        <option value="recoletaregular, serif">Recoleta</option>
        <option value="Arial, sans-serif">Arial</option>
        <option value="Georgia, serif">Georgia</option>
        <option value="Times New Roman, serif">Times</option>
        <option value="Courier New, monospace">Courier</option>
      </select>
      <select
        value={editor.getAttributes('textStyle').fontSize || ''}
        onChange={(e) => {
          if (e.target.value) editor.chain().focus().setFontSize(e.target.value).run();
          else editor.chain().focus().unsetFontSize().run();
        }}
        className="h-7 text-xs border border-gray-200 rounded px-1 bg-white text-gray-600 focus:outline-none focus:ring-1 focus:ring-[#026857] ml-0.5"
        aria-label="Font size"
      >
        <option value="">Size</option>
        {[12, 14, 16, 18, 20, 24, 28, 32, 36, 48].map((s) => (
          <option key={s} value={`${s}px`}>{s}</option>
        ))}
      </select>

      <Divider />

      {/* Lists */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive('bulletList')}
        label="Bullet list"
      >
        <List size={iconSize} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive('orderedList')}
        label="Ordered list"
      >
        <ListOrdered size={iconSize} />
      </ToolbarButton>

      <Divider />

      {/* Block elements */}
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive('blockquote')}
        label="Blockquote"
      >
        <Quote size={iconSize} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        isActive={editor.isActive('codeBlock')}
        label="Code block"
      >
        <Code size={iconSize} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        label="Horizontal rule"
      >
        <Minus size={iconSize} />
      </ToolbarButton>

      <Divider />

      {/* Alignment */}
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        isActive={editor.isActive({ textAlign: 'left' })}
        label="Align left"
      >
        <AlignLeft size={iconSize} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        isActive={editor.isActive({ textAlign: 'center' })}
        label="Align center"
      >
        <AlignCenter size={iconSize} />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        isActive={editor.isActive({ textAlign: 'right' })}
        label="Align right"
      >
        <AlignRight size={iconSize} />
      </ToolbarButton>

      <Divider />

      {/* Media & embeds */}
      <ToolbarButton onClick={handleLink} isActive={editor.isActive('link')} label="Link">
        <Link size={iconSize} />
      </ToolbarButton>
      <ToolbarButton onClick={() => openInput('image')} label="Image">
        <ImageIcon size={iconSize} />
      </ToolbarButton>
      <ToolbarButton onClick={handleTable} label="Table">
        <Table size={iconSize} />
      </ToolbarButton>
      <ToolbarButton onClick={() => openInput('embed')} label="Embed iframe">
        <MonitorPlay size={iconSize} />
      </ToolbarButton>

      {activeInput && (
        <ToolbarUrlInput
          type={activeInput}
          value={inputUrl}
          onChange={setInputUrl}
          onApply={applyInput}
          onCancel={closeInput}
        />
      )}
    </div>
  );
}
